import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '../i18n/i18n';
import { useAuth } from '../components/AuthProvider';
import { useCurrentLanguage } from '../components/LanguageRedirect';
import { Mail, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

interface EmailVerificationPageProps {
  userEmail?: string;
  onResendVerification?: (email: string) => Promise<void>;
  onVerificationComplete?: () => void;
}

export const EmailVerificationPage: React.FC<EmailVerificationPageProps> = ({
  userEmail,
  onResendVerification,
  onVerificationComplete,
}) => {
  const navigate = useNavigate();
  const currentLang = useCurrentLanguage();
  const { toast } = useToast();
  const { t } = useI18n();
  const { refreshUser } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'verified' | 'error'>('pending');
  const [email, setEmail] = useState(userEmail || '');
  
  // SEO: Private page - prevent indexing
  const seoContent = useSEO({
    title: 'Email Verification | Finanzly',
    description: 'Verify your email address to access your Finanzly account.',
    noindex: true,
  });

  // Parse URL parameters manually
  const urlParams = new URLSearchParams(window.location.search);
  const verificationToken = urlParams.get('token');
  const mode = urlParams.get('mode');
  const oobCode = urlParams.get('oobCode'); // Firebase action code
  const apiKey = urlParams.get('apiKey'); // Firebase API key

  useEffect(() => {
    if (mode === 'verifyEmail') {
      if (oobCode) {
        // Handle Firebase verification
        handleFirebaseEmailVerification(oobCode);
      } else if (verificationToken) {
        // Handle legacy custom verification
        handleEmailVerification(verificationToken);
      }
    }
  }, [mode, verificationToken, oobCode]);

  const handleFirebaseEmailVerification = async (actionCode: string) => {
    try {
      const { verifyEmailWithActionCode } = await import('../lib/firebase');
      await verifyEmailWithActionCode(actionCode);

      // Refresh user state to get updated email verification status
      await refreshUser();

      setVerificationStatus('verified');
      toast({
        title: t('auth.verification.verificationSuccess'),
        description: t('auth.verification.verificationSuccess'),
      });
      
      // Call completion callback if provided
      if (onVerificationComplete) {
        onVerificationComplete();
      }

      // Redirect to login after a delay
      setTimeout(() => {
        navigate(`/${currentLang}`);
      }, 3000);
    } catch (error: any) {
      console.error('Firebase email verification error:', error);
      setVerificationStatus('error');
      toast({
        title: t('auth.verification.verificationError'),
        description: error.message || t('auth.verification.verificationError'),
        variant: 'destructive',
      });
    }
  };

  const handleEmailVerification = async (token: string) => {
    try {
      const { verifyEmail } = await import('../lib/firebase');
      const data = await verifyEmail(token);

      // Refresh user state to get updated email verification status
      await refreshUser();

      setVerificationStatus('verified');
      toast({
        title: t('auth.verification.verificationSuccess'),
        description: t('auth.verification.verificationSuccess'),
      });
      
      // Call completion callback if provided
      if (onVerificationComplete) {
        onVerificationComplete();
      }

      // Redirect to login after a delay
      setTimeout(() => {
        navigate(`/${currentLang}`);
      }, 3000);
    } catch (error: any) {
      setVerificationStatus('error');
      toast({
        title: t('auth.verification.verificationError'),
        description: error.message || t('auth.verification.verificationError'),
        variant: 'destructive',
      });
    }
  };

  const handleResendVerification = async () => {
    if (!email || !onResendVerification) return;

    setIsResending(true);
    try {
      await onResendVerification(email);
      toast({
        title: t('auth.verification.resendSuccess'),
        description: t('auth.verification.resendSuccess'),
      });
    } catch (error) {
      toast({
        title: t('auth.verification.resendError'),
        description: t('auth.verification.resendError'),
        variant: 'destructive',
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleGoToLogin = () => {
    navigate(`/${currentLang}`);
  };

  // Render verification result if it's a callback
  if (mode === 'verifyEmail' && (verificationToken || oobCode)) {
    return (
      <div className="flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 min-h-[calc(100vh-200px)]">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            {verificationStatus === 'verified' && (
              <>
                <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
                <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                  {t('auth.verification.verificationSuccess')}
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  {t('auth.verification.verificationSuccess')}
                </p>
              </>
            )}
            
            {verificationStatus === 'error' && (
              <>
                <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
                <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                  {t('auth.verification.verificationError')}
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  {t('auth.verification.verificationError')}
                </p>
                <Button
                  onClick={handleGoToLogin}
                  className="mt-4 bg-[#EA580C] hover:bg-[#C2410C]"
                >
                  {t('auth.verification.goToLogin')}
                </Button>
              </>
            )}

            {verificationStatus === 'pending' && (
              <>
                <RefreshCw className="mx-auto h-12 w-12 text-blue-500 animate-spin" />
                <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                  Verifying your email...
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  Please wait while we verify your email address.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render verification waiting page
  return (
    <>
      {seoContent}
      <div className="flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 min-h-[calc(100vh-200px)]">
        <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Mail className="mx-auto h-12 w-12 text-[#EA580C]" />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {t('auth.verification.title')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('auth.verification.subtitle')}
          </p>
          {email && (
            <p className="mt-1 text-sm font-medium text-[#EA580C]">
              {email}
            </p>
          )}
        </div>

        <div className="mt-8 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <Mail className="h-5 w-5 text-blue-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  {t('auth.verification.checkEmail')}
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>{t('auth.verification.description')}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col space-y-3">
            {onResendVerification && (
              <Button
                type="button"
                variant="outline"
                onClick={handleResendVerification}
                disabled={isResending || !email}
                className="w-full"
              >
                {isResending ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  t('auth.verification.resendButton')
                )}
              </Button>
            )}

            <Button
              type="button"
              variant="link"
              onClick={handleGoToLogin}
              className="text-[#EA580C] hover:text-[#C2410C]"
            >
              {t('auth.verification.goToLogin')}
            </Button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};
